import {
  dehydrate,
  hydrate,
  type DehydratedState,
  type QueryClient,
  type Query,
} from "@tanstack/react-query";
import { INVESTING_PROFILE_PREFERENCE_KEY } from "@repo/types";
import superjson from "superjson";
import { schedulePostPaint } from "~/lib/schedule-post-paint";
import {
  AUTH_SCOPE_QUERY_KEY_SEGMENT,
  SESSION_SCOPE_QUERY_KEY_SEGMENT,
  queryKeys,
} from "~/lib/query-keys";

const QUERY_PERSISTENCE_STORAGE_KEY = "glider:webapp-v2:react-query-persist:v1";
// v3: persisted queries now restore against per-family max ages instead of a
// single snapshot-wide max age.
const QUERY_PERSISTENCE_BUSTER = "2026-07-19:react-query-persist:v3";
const QUERY_PERSISTENCE_DB_NAME = "glider-webapp-v2";
const QUERY_PERSISTENCE_DB_STORE_NAME = "app-cache";
const REFERENCE_MAX_AGE_MS = 7 * 24 * 60 * 60_000;
const MARKET_MAX_AGE_MS = 12 * 60 * 60_000;
const QUERY_PERSISTENCE_WRITE_DEBOUNCE_MS = 250;

interface QueryPersistencePolicy {
  readonly maxAgeMs: number;
}

interface QueryPersistencePolicyMatcher {
  readonly matches: (queryKey: readonly unknown[]) => boolean;
  readonly policy: QueryPersistencePolicy;
}

const REFERENCE_QUERY_PERSISTENCE_POLICY: QueryPersistencePolicy = {
  maxAgeMs: REFERENCE_MAX_AGE_MS,
};
const MARKET_QUERY_PERSISTENCE_POLICY: QueryPersistencePolicy = {
  maxAgeMs: MARKET_MAX_AGE_MS,
};

type QueryPersistenceStorage = {
  getItem: (key: string) => Promise<string | null>;
  removeItem: (key: string) => Promise<void>;
  setItem: (key: string, value: string) => Promise<void>;
};

interface PersistedQueryCacheRecord {
  buster: string;
  dehydratedState: DehydratedState;
  persistedAt: number;
}

interface RestorePersistedQueryClientOptions {
  now?: number;
  queryClient: QueryClient;
  storage: QueryPersistenceStorage;
}

interface PersistQueryClientSnapshotOptions {
  queryClient: QueryClient;
  storage: QueryPersistenceStorage;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
}

function asReadonlyArray(value: unknown): readonly unknown[] | null {
  return Array.isArray(value) ? value : null;
}

function isDehydratedState(value: unknown): value is DehydratedState {
  const record = asRecord(value);
  return (
    record !== null &&
    Array.isArray(record.mutations) &&
    Array.isArray(record.queries)
  );
}

function isPersistedQueryCacheRecord(
  value: unknown,
): value is PersistedQueryCacheRecord {
  const record = asRecord(value);
  return (
    record !== null &&
    typeof record.buster === "string" &&
    typeof record.persistedAt === "number" &&
    Number.isFinite(record.persistedAt) &&
    isDehydratedState(record.dehydratedState)
  );
}

function matchesQueryKeyPrefix(
  queryKey: readonly unknown[],
  prefix: readonly unknown[],
): boolean {
  if (queryKey.length < prefix.length) {
    return false;
  }

  for (let index = 0; index < prefix.length; index += 1) {
    if (queryKey[index] !== prefix[index]) {
      return false;
    }
  }

  return true;
}

function getBaseQueryKey(queryKey: readonly unknown[]): readonly unknown[] {
  const authScopeIndex = queryKey.lastIndexOf(AUTH_SCOPE_QUERY_KEY_SEGMENT);
  const sessionScopeIndex = queryKey.lastIndexOf(
    SESSION_SCOPE_QUERY_KEY_SEGMENT,
  );
  const scopeIndex = Math.max(authScopeIndex, sessionScopeIndex);
  return scopeIndex >= 0 ? queryKey.slice(0, scopeIndex) : queryKey;
}

function hasAuthScopedQueryKey(queryKey: readonly unknown[]): boolean {
  return (
    queryKey.includes(AUTH_SCOPE_QUERY_KEY_SEGMENT) ||
    queryKey.includes(SESSION_SCOPE_QUERY_KEY_SEGMENT)
  );
}

const QUERY_PERSISTENCE_POLICY_MATCHERS = [
  {
    matches: (queryKey) =>
      queryKey.length === queryKeys.directIndex.catalog.length &&
      matchesQueryKeyPrefix(queryKey, queryKeys.directIndex.catalog),
    policy: REFERENCE_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.platformApi.appConfig),
    policy: REFERENCE_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.platformApi.activePromotions),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.featureFlags.userFlags),
    policy: REFERENCE_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, [
        "platform-api",
        "displayPreferences.getDisplayProfile",
      ]),
    policy: REFERENCE_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, [
        "platform-api",
        "preferences.getEffective",
      ]) && queryKey.includes(INVESTING_PROFILE_PREFERENCE_KEY),
    policy: REFERENCE_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.assetDomain.assets),
    policy: REFERENCE_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.asset.details),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.asset.lookup),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.stocks.bootstrap),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.stocks.browse),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.stocks.assetUniverseList),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.stocks.strategy),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  // The public /stocks fold. All of these are anonymous market data — no
  // auth/session scope segment — so a returning visitor can paint prices from
  // IndexedDB instead of waiting on the network. A restored price is never
  // labelled live: the market strip derives its freshness from the snapshot's
  // own timestamp against the visible truth clock, so an hours-old restored
  // snapshot renders "delayed" or "stale" until the settle request lands.
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(
        queryKey,
        queryKeys.tradFiMarketData.stockSnapshots,
      ),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(
        queryKey,
        queryKeys.tradFiMarketData.stockMarketMovers,
      ),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.tradFiMarketData.marketStatus),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(
        queryKey,
        queryKeys.tradFiMarketData.stockTrailingHourChange,
      ),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(
        queryKey,
        queryKeys.tradFiMarketData.stockLastSessionChange,
      ),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  // Tradeable-asset news feeds the /explore and /stocks news rails. Headlines
  // carry their own publication time in the UI, so a restored list is
  // self-labelling; nothing monetary rides on it.
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(
        queryKey,
        queryKeys.tradFiMarketData.tradeableAssetNews,
      ),
    policy: MARKET_QUERY_PERSISTENCE_POLICY,
  },
  // Symbol identity resolution is reference data: the ticker → canonical asset
  // mapping does not move, and it gates the whole /stocks/:symbol route.
  {
    matches: (queryKey) =>
      matchesQueryKeyPrefix(queryKey, queryKeys.symbol.resolution),
    policy: REFERENCE_QUERY_PERSISTENCE_POLICY,
  },
] satisfies readonly QueryPersistencePolicyMatcher[];

function resolveQueryPersistencePolicy(
  queryKey: readonly unknown[],
): QueryPersistencePolicy | null {
  if (hasAuthScopedQueryKey(queryKey)) {
    return null;
  }

  const baseQueryKey = getBaseQueryKey(queryKey);
  for (const matcher of QUERY_PERSISTENCE_POLICY_MATCHERS) {
    if (matcher.matches(baseQueryKey)) {
      return matcher.policy;
    }
  }

  return null;
}

export function shouldPersistQueryKey(queryKey: readonly unknown[]): boolean {
  return resolveQueryPersistencePolicy(queryKey) !== null;
}

function buildPersistedQueryCacheRecord(
  queryClient: QueryClient,
): PersistedQueryCacheRecord {
  return {
    buster: QUERY_PERSISTENCE_BUSTER,
    dehydratedState: dehydrate(queryClient, {
      shouldDehydrateQuery: (query) =>
        query.state.status === "success" &&
        shouldPersistQueryKey(query.queryKey),
    }),
    persistedAt: Date.now(),
  };
}

function filterRestorableQueries(
  dehydratedState: DehydratedState,
  snapshotAgeMs: number,
): DehydratedState {
  return {
    ...dehydratedState,
    mutations: [],
    queries: dehydratedState.queries.filter((query) => {
      const queryKey = asReadonlyArray(query.queryKey);
      if (queryKey === null) {
        return false;
      }

      const policy = resolveQueryPersistencePolicy(queryKey);
      return policy !== null && snapshotAgeMs <= policy.maxAgeMs;
    }),
  };
}

function createLocalStoragePersistenceStorage(
  storage: Storage,
): QueryPersistenceStorage {
  return {
    getItem: async (key) => storage.getItem(key),
    removeItem: async (key) => {
      storage.removeItem(key);
    },
    setItem: async (key, value) => {
      storage.setItem(key, value);
    },
  };
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("error", () => {
      reject(request.error ?? new Error("IndexedDB request failed."));
    });
    request.addEventListener("success", () => {
      resolve(request.result);
    });
  });
}

function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("abort", () => {
      reject(transaction.error ?? new Error("IndexedDB transaction aborted."));
    });
    transaction.addEventListener("complete", () => {
      resolve();
    });
    transaction.addEventListener("error", () => {
      reject(transaction.error ?? new Error("IndexedDB transaction failed."));
    });
  });
}

async function createIndexedDbPersistenceStorage(): Promise<QueryPersistenceStorage | null> {
  if (typeof indexedDB === "undefined") {
    return null;
  }

  try {
    const openRequest = indexedDB.open(QUERY_PERSISTENCE_DB_NAME, 1);
    openRequest.addEventListener("upgradeneeded", () => {
      const database = openRequest.result;
      if (
        !database.objectStoreNames.contains(QUERY_PERSISTENCE_DB_STORE_NAME)
      ) {
        database.createObjectStore(QUERY_PERSISTENCE_DB_STORE_NAME);
      }
    });

    const database = await requestToPromise(openRequest);

    return {
      getItem: async (key) => {
        const transaction = database.transaction(
          QUERY_PERSISTENCE_DB_STORE_NAME,
          "readonly",
        );
        const result = await requestToPromise(
          transaction.objectStore(QUERY_PERSISTENCE_DB_STORE_NAME).get(key),
        );
        return typeof result === "string" ? result : null;
      },
      removeItem: async (key) => {
        const transaction = database.transaction(
          QUERY_PERSISTENCE_DB_STORE_NAME,
          "readwrite",
        );
        transaction.objectStore(QUERY_PERSISTENCE_DB_STORE_NAME).delete(key);
        await transactionToPromise(transaction);
      },
      setItem: async (key, value) => {
        const transaction = database.transaction(
          QUERY_PERSISTENCE_DB_STORE_NAME,
          "readwrite",
        );
        transaction
          .objectStore(QUERY_PERSISTENCE_DB_STORE_NAME)
          .put(value, key);
        await transactionToPromise(transaction);
      },
    };
  } catch {
    return null;
  }
}

async function getBrowserPersistenceStorage(): Promise<QueryPersistenceStorage | null> {
  const indexedDbStorage = await createIndexedDbPersistenceStorage();
  if (indexedDbStorage) {
    return indexedDbStorage;
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    return createLocalStoragePersistenceStorage(window.localStorage);
  } catch {
    return null;
  }
}

export async function persistQueryClientSnapshot(
  options: PersistQueryClientSnapshotOptions,
): Promise<void> {
  const snapshot = buildPersistedQueryCacheRecord(options.queryClient);
  if (snapshot.dehydratedState.queries.length === 0) {
    await options.storage.removeItem(QUERY_PERSISTENCE_STORAGE_KEY);
    return;
  }

  await options.storage.setItem(
    QUERY_PERSISTENCE_STORAGE_KEY,
    superjson.stringify(snapshot),
  );
}

export async function restorePersistedQueryClient(
  options: RestorePersistedQueryClientOptions,
): Promise<void> {
  const rawSnapshot = await options.storage.getItem(
    QUERY_PERSISTENCE_STORAGE_KEY,
  );
  if (!rawSnapshot) {
    return;
  }

  let parsedSnapshot: unknown;
  try {
    parsedSnapshot = superjson.parse<unknown>(rawSnapshot);
  } catch {
    await options.storage.removeItem(QUERY_PERSISTENCE_STORAGE_KEY);
    return;
  }

  if (!isPersistedQueryCacheRecord(parsedSnapshot)) {
    await options.storage.removeItem(QUERY_PERSISTENCE_STORAGE_KEY);
    return;
  }

  if (parsedSnapshot.buster !== QUERY_PERSISTENCE_BUSTER) {
    await options.storage.removeItem(QUERY_PERSISTENCE_STORAGE_KEY);
    return;
  }

  const now = options.now ?? Date.now();
  const restorableState = filterRestorableQueries(
    parsedSnapshot.dehydratedState,
    now - parsedSnapshot.persistedAt,
  );
  if (restorableState.queries.length === 0) {
    await options.storage.removeItem(QUERY_PERSISTENCE_STORAGE_KEY);
    return;
  }

  hydrate(options.queryClient, restorableState);
}

function subscribePersistedQueryClient(
  options: PersistQueryClientSnapshotOptions,
): () => void {
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelIdlePersist: (() => void) | null = null;
  let disposed = false;
  let isPersisting = false;
  let shouldPersistAgain = false;
  const persistedVersions = new Map<string, Query["state"]>();
  for (const query of options.queryClient.getQueryCache().getAll()) {
    if (
      query.state.status === "success" &&
      shouldPersistQueryKey(query.queryKey)
    ) {
      persistedVersions.set(query.queryHash, query.state);
    }
  }

  const runPersist = async () => {
    if (disposed) {
      return;
    }
    if (isPersisting) {
      shouldPersistAgain = true;
      return;
    }

    isPersisting = true;

    try {
      await persistQueryClientSnapshot(options);
    } finally {
      isPersisting = false;

      if (shouldPersistAgain) {
        shouldPersistAgain = false;
        schedulePersist();
      }
    }
  };

  const schedulePersist = () => {
    if (disposed) {
      return;
    }
    if (isPersisting) {
      shouldPersistAgain = true;
      return;
    }
    // Coalesce from the first change, so a busy feed cannot postpone the
    // write forever. A later idle slice keeps serialization out of input work.
    if (persistTimer !== null || cancelIdlePersist !== null) {
      return;
    }

    persistTimer = setTimeout(() => {
      persistTimer = null;
      cancelIdlePersist = schedulePostPaint(
        () => {
          cancelIdlePersist = null;
          void runPersist().catch(() => {
            // Best-effort persistence: storage failure must not block the app.
          });
        },
        0,
        1_000,
      );
    }, QUERY_PERSISTENCE_WRITE_DEBOUNCE_MS);
  };

  const unsubscribe = options.queryClient.getQueryCache().subscribe((event) => {
    if (
      (event.type !== "added" &&
        event.type !== "updated" &&
        event.type !== "removed") ||
      !shouldPersistQueryKey(event.query.queryKey)
    ) {
      return;
    }
    const { query } = event;
    const previous = persistedVersions.get(query.queryHash);
    if (event.type === "removed" || query.state.status !== "success") {
      if (persistedVersions.delete(query.queryHash)) {
        schedulePersist();
      }
      return;
    }
    const next = query.state;
    persistedVersions.set(query.queryHash, next);
    if (
      !previous ||
      previous.data !== next.data ||
      previous.dataUpdatedAt !== next.dataUpdatedAt ||
      previous.isInvalidated !== next.isInvalidated
    ) {
      schedulePersist();
    }
  });

  schedulePersist();

  return () => {
    disposed = true;
    unsubscribe();
    cancelIdlePersist?.();
    if (persistTimer !== null) {
      clearTimeout(persistTimer);
    }
  };
}

let activeQueryPersistenceCleanup: (() => void) | null = null;
let browserQueryPersistenceReady: Promise<void> | null = null;
let resolveBrowserQueryPersistenceReady: (() => void) | null = null;

function getBrowserQueryPersistenceReadyPromise(): Promise<void> {
  if (browserQueryPersistenceReady === null) {
    browserQueryPersistenceReady = new Promise<void>((resolve) => {
      resolveBrowserQueryPersistenceReady = resolve;
    });
  }

  return browserQueryPersistenceReady;
}

/**
 * Resolves once `initializeBrowserQueryPersistence` has finished restoring the
 * IndexedDB snapshot (successfully or not), so a route loader can read the
 * restored cache instead of racing it to the network. Resolves immediately on
 * the server, where there is nothing to restore.
 *
 * Callers must still bound their wait: hydration is deliberately scheduled
 * post-paint, and a cold visitor has nothing to restore at all.
 */
export function whenBrowserQueryPersistenceReady(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  return getBrowserQueryPersistenceReadyPromise();
}

function markBrowserQueryPersistenceReady(): void {
  getBrowserQueryPersistenceReadyPromise();
  resolveBrowserQueryPersistenceReady?.();
  resolveBrowserQueryPersistenceReady = null;
}

export async function initializeBrowserQueryPersistence(
  queryClient: QueryClient,
): Promise<void> {
  try {
    const storage = await getBrowserPersistenceStorage();
    if (!storage) {
      return;
    }

    await restorePersistedQueryClient({
      queryClient,
      storage,
    });

    activeQueryPersistenceCleanup?.();
    activeQueryPersistenceCleanup = subscribePersistedQueryClient({
      queryClient,
      storage,
    });
  } catch {
    activeQueryPersistenceCleanup?.();
    activeQueryPersistenceCleanup = null;
    return;
  } finally {
    // Waiters are released whether or not a snapshot existed: "ready" means
    // "the cache is no longer about to change underneath you", not "restored".
    markBrowserQueryPersistenceReady();
  }
}

export const queryPersistenceTestUtils = {
  buster: QUERY_PERSISTENCE_BUSTER,
  createLocalStoragePersistenceStorage,
  filterRestorableQueries,
  marketMaxAgeMs: MARKET_MAX_AGE_MS,
  referenceMaxAgeMs: REFERENCE_MAX_AGE_MS,
  /** Drops the module-level readiness latch so each test starts unresolved. */
  resetBrowserQueryPersistenceReady: () => {
    activeQueryPersistenceCleanup?.();
    activeQueryPersistenceCleanup = null;
    browserQueryPersistenceReady = null;
    resolveBrowserQueryPersistenceReady = null;
  },
  resolveQueryPersistencePolicy,
  subscribePersistedQueryClient,
  storageKey: QUERY_PERSISTENCE_STORAGE_KEY,
};
